import React, { useMemo } from "react";
import styled from "styled-components";
import { Switch, Select } from "antd";

const DAYS = [
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
];

// Generate time options in 15-minute increments
const generateTimeOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const h = hour % 12 || 12;
      const ampm = hour < 12 ? "AM" : "PM";
      const m = minute.toString().padStart(2, "0");
      const display = `${h}:${m} ${ampm}`;
      const value = `${hour.toString().padStart(2, "0")}:${m}`;
      options.push({ label: display, value });
    }
  }
  return options;
};

const TIME_OPTIONS = generateTimeOptions();

// Convert HH:mm to display format (h:mm AM/PM)
const formatTimeForDisplay = (timeStr) => {
  if (!timeStr) return null;
  const [hours, minutes] = timeStr.split(":");
  const hour = parseInt(hours, 10);
  const h = hour % 12 || 12;
  const ampm = hour < 12 ? "AM" : "PM";
  return `${h}:${minutes} ${ampm}`;
};

// Convert display format back to HH:mm
const parseTimeFromDisplay = (displayStr) => {
  if (!displayStr) return null;
  const match = displayStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!match) return null;
  let hour = parseInt(match[1], 10);
  const minute = match[2];
  const ampm = match[3].toUpperCase();
  if (ampm === "PM" && hour !== 12) hour += 12;
  if (ampm === "AM" && hour === 12) hour = 0;
  return `${hour.toString().padStart(2, "0")}:${minute}`;
};

export const defaultAvailabilitySchedule = () => ({
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  days: {
    mon: { enabled: true, start: "09:00", end: "17:00" },
    tue: { enabled: true, start: "09:00", end: "17:00" },
    wed: { enabled: true, start: "09:00", end: "17:00" },
    thu: { enabled: true, start: "09:00", end: "17:00" },
    fri: { enabled: true, start: "09:00", end: "17:00" },
    sat: { enabled: false, start: "10:00", end: "14:00" },
    sun: { enabled: false, start: "10:00", end: "14:00" },
  },
});

/**
 * AvailabilityScheduler
 *
 * Landlord-facing scheduler for tour/showing availability.
 * Controlled component: value + onChange.
 *
 * Shape:
 * {
 *   timezone: "America/Chicago",
 *   days: { mon: { enabled, start, end }, ... }
 * }
 */
const AvailabilityScheduler = ({ value, onChange }) => {
  const schedule = useMemo(() => {
    return value && value.days ? value : defaultAvailabilitySchedule();
  }, [value]);

  const update = (patch) => {
    const next = {
      ...schedule,
      ...patch,
    };
    onChange?.(next);
  };

  const updateDay = (dayKey, patch) => {
    update({
      days: {
        ...(schedule.days || {}),
        [dayKey]: {
          ...(schedule.days?.[dayKey] || {}),
          ...patch,
        },
      },
    });
  };

  return (
    <Wrapper>
      {DAYS.map((d) => {
        const day = schedule.days?.[d.key] || {};
        const startDisplay = formatTimeForDisplay(day.start);
        const endDisplay = formatTimeForDisplay(day.end);

        return (
          <DayRow key={d.key}>
            <DayHeader>
              <DayLabel>{d.label}</DayLabel>
              <Switch
                checked={!!day.enabled}
                onChange={(checked) => updateDay(d.key, { enabled: checked })}
              />
            </DayHeader>

            <TimeRow>
              <TimeSelect
                value={day.start}
                placeholder="Start"
                disabled={!day.enabled}
                onChange={(selectedValue) => {
                  // Select passes the option's value (HH:mm format), use it directly
                  if (selectedValue && /^\d{2}:\d{2}$/.test(selectedValue)) {
                    updateDay(d.key, { start: selectedValue });
                  } else {
                    // Fallback: try parsing from display format if needed
                    const timeValue = parseTimeFromDisplay(selectedValue);
                    if (timeValue) {
                      updateDay(d.key, { start: timeValue });
                    }
                  }
                }}
                options={TIME_OPTIONS}
                popupMatchSelectWidth={false}
                getPopupContainer={(trigger) => trigger.parentElement}
              />
              <Dash>-</Dash>
              <TimeSelect
                value={day.end}
                placeholder="End"
                disabled={!day.enabled}
                onChange={(selectedValue) => {
                  // Select passes the option's value (HH:mm format), use it directly
                  if (selectedValue && /^\d{2}:\d{2}$/.test(selectedValue)) {
                    updateDay(d.key, { end: selectedValue });
                  } else {
                    // Fallback: try parsing from display format if needed
                    const timeValue = parseTimeFromDisplay(selectedValue);
                    if (timeValue) {
                      updateDay(d.key, { end: timeValue });
                    }
                  }
                }}
                options={TIME_OPTIONS}
                popupMatchSelectWidth={false}
                getPopupContainer={(trigger) => trigger.parentElement}
              />
            </TimeRow>
          </DayRow>
        );
      })}
      <TimezoneNote>Timezone: {schedule.timezone}</TimezoneNote>
    </Wrapper>
  );
};

export default AvailabilityScheduler;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DayRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (min-width: 641px) {
    display: grid;
    grid-template-columns: 44px 52px 1fr 16px 1fr;
    align-items: center;
    gap: 8px;
  }
`;

const DayHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (min-width: 641px) {
    display: contents;
  }
`;

const DayLabel = styled.div`
  font-weight: 800;
  color: var(--color-text);
  font-size: 0.95rem;

  @media (max-width: 640px) {
    font-size: 0.9rem;
  }
`;

const TimeRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 12px 1fr;
  align-items: center;
  gap: 8px;

  @media (min-width: 641px) {
    grid-column: 3 / -1;
  }

  @media (max-width: 640px) {
    margin-left: 0;
  }
`;

const Dash = styled.div`
  text-align: center;
  color: var(--color-text-secondary);
  font-weight: 700;
  flex-shrink: 0;
`;

const TimezoneNote = styled.div`
  margin-top: 4px;
  font-size: 0.85rem;
  color: var(--color-text-secondary);
`;

const TimeSelect = styled(Select)`
  min-width: 0;
  width: 100%;

  .ant-select-selector {
    height: 40px !important;
    border-radius: 8px !important;
    border: 1px solid var(--color-border) !important;
    font-family: var(--font-secondary) !important;
    font-weight: 600 !important;
    font-size: 0.9rem !important;
  }

  .ant-select-selection-item {
    line-height: 38px !important;
    color: var(--color-text) !important;
    font-weight: 600 !important;
  }

  .ant-select-selection-placeholder {
    line-height: 38px !important;
    color: var(--color-text-light) !important;
  }

  &:hover .ant-select-selector {
    border-color: var(--color-primary) !important;
  }

  &.ant-select-focused .ant-select-selector {
    border-color: var(--color-primary) !important;
    box-shadow: 0 0 0 2px rgba(44, 95, 45, 0.1) !important;
  }

  &.ant-select-disabled .ant-select-selector {
    background: var(--color-bg-tertiary) !important;
    border-color: var(--color-border-light) !important;
    cursor: not-allowed !important;
  }

  &.ant-select-disabled .ant-select-selection-item {
    color: var(--color-text-light) !important;
  }

  @media (max-width: 640px) {
    .ant-select-selector {
      height: 38px !important;
      font-size: 0.85rem !important;
    }

    .ant-select-selection-item,
    .ant-select-selection-placeholder {
      line-height: 36px !important;
    }
  }
`;
